import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { BarChart3, Layers, Bot, BookOpen, Bell, ChevronLeft, ShieldCheck } from 'lucide-react';
import { useTalentMapAccess } from '@/hooks/usePerformance';

export function PerformanceAdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: tmAccess } = useTalentMapAccess();

  const adminNavItems = [
    { label: t('performance.admin.layout.dashboard'), href: '/performance/admin', icon: BarChart3, end: true },
    { label: t('performance.admin.layout.epics'), href: '/performance/admin/epics', icon: Layers, end: false },
    { label: t('performance.admin.layout.assistants'), href: '/performance/admin/assistants', icon: Bot, end: false },
    { label: t('performance.admin.layout.knowledge_base'), href: '/performance/admin/knowledge', icon: BookOpen, end: false },
    { label: t('performance.admin.layout.notifications'), href: '/performance/admin/notifications', icon: Bell, end: false },
    ...(tmAccess?.isGovernanceAdmin
      ? [{ label: t('performance.admin.layout.governance', { defaultValue: 'Permisos' }), href: '/performance/admin/governance', icon: ShieldCheck, end: false }]
      : []),
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Admin header + tab nav */}
      <div className="border-b bg-card shrink-0">
        {/* Breadcrumb row */}
        <div className="flex items-center gap-2 px-4 sm:px-6 pt-4">
          <button
            onClick={() => navigate('/performance')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {t('performance.admin.back', { defaultValue: 'Performance' })}
          </button>
          <span className="text-muted-foreground/40">/</span>
          <span className="text-sm font-semibold text-foreground">{t('performance.admin.title')}</span>
        </div>

        {/* Tab row */}
        <nav className="flex items-end gap-0 px-4 sm:px-6 mt-2 overflow-x-auto scrollbar-none">
          {adminNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors shrink-0',
                    isActive
                      ? 'border-primary text-primary font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                  )
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Page content */}
      <div className="flex-1 overflow-auto min-h-0">
        <Outlet />
      </div>
    </div>
  );
}
